import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Form as AntdForm,
  Button,
  Typography,
} from 'antd';
import dayjs from 'dayjs';

import { AppBarCMS } from '../../../../basic/index.jsx';
import { CollapseBlock, QuestionModal, Spinner } from '../../../../common/index.jsx';
import { SeriousDeleteQuestion } from '../../../../common/DeleteQuestion/SeriousDeleteQuestion.jsx';
import IngProjectsSettingsForm from './IngProjectsSettingsForm.jsx';
import {
 useGetIngProjectsSettingsQuery,
 useSaveIngProjectsSettingsMutation,
 usePrepareNewYearMutation,
} from '../../../../../redux/services/ing_projects_settings.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';

const IngProjectsSettingsPage = () => {
  const { t } = useTranslation();
  const [form] = AntdForm.useForm();
  const [prepareNewYearOpen, setPrepareNewYearOpen] = useState(false);

  const {
    saveSuccess: showSaveSuccess,
    saveFailed,
    successMessage,
    errorMessage,
  } = useToastMessages();

  const { data: settings, isLoading: isLoadingSettings } =
    useGetIngProjectsSettingsQuery(undefined, { refetchOnMountOrArgChange: true });

  const [saveSettings, { isLoading: isSaving }] =
    useSaveIngProjectsSettingsMutation();

  const [prepareNewYear, { isLoading: isPreparing }] =
    usePrepareNewYearMutation();

  const initials = useMemo(() => {
    if (!settings) {
      return {
        projectModificationRange: undefined,
        studentApplicationRange: undefined,
        panelStoryRange: undefined,
      };
    }

    return {
      projectModificationRange:
        settings.date_project_modification_from &&
        settings.date_project_modification_to
          ? [
              dayjs(settings.date_project_modification_from),
              dayjs(settings.date_project_modification_to),
            ]
          : undefined,
      studentApplicationRange:
        settings.date_student_application_from &&
        settings.date_student_application_to
          ? [
              dayjs(settings.date_student_application_from),
              dayjs(settings.date_student_application_to),
            ]
          : undefined,
      panelStoryRange:
        settings.date_panel_story_from && settings.date_panel_story_to
          ? [
              dayjs(settings.date_panel_story_from),
              dayjs(settings.date_panel_story_to),
            ]
          : undefined,
    };
  }, [settings]);

  useEffect(() => {
    if (settings) {
      form.setFieldsValue(initials);
    }
  }, [settings, initials, form]);

  const title = t('Nastavenia modulu inž. projektov');

  useEffect(() => {
    document.title = title + ' | FRI UNIZA';
  }, [title]);

  const academicYear = settings?.academic_year + 1;
  const unpublishedStudyPrograms = settings?.unpublished_study_programs || [];
  const hasUnpublishedStudyPrograms = unpublishedStudyPrograms.length > 0;

  const handlePrepareNewYear = async () => {
    try {
      const result = await prepareNewYear().unwrap();
      const data = result?.data;
      successMessage({
        message: t('Nový školský rok bol pripravený'),
        description: `${t('Rok')}: ${data?.new_year}/${data?.new_year + 1}, ${t('zmazaných prihlášok')}: ${data?.deleted_applications}, ${t('zmazaných prijatých študentov')}: ${data?.deleted_accepted_students}`,
      });
    } catch (error) {
      errorMessage({
        message: t('Príprava nového roku neúspešná'),
        description: error?.data?.message || t('Nastala chyba.'),
      });
    }
  };

  const handleSaveSettings = async (values) => {
    try {
      await saveSettings(values).unwrap();
      showSaveSuccess();
    } catch (error) {
      saveFailed(error);
    }
  };

  if (isLoadingSettings) {
    return <Spinner />;
  }

  return (
    <>
      <AppBarCMS title={title} />
      <div style={{ margin: '10px' }}>
        <CollapseBlock title={t('Nastavenia období')} collapsible={true}>
          <IngProjectsSettingsForm
            initials={initials}
            form={form}
            onSubmit={handleSaveSettings}
            loading={isSaving}
          />
        </CollapseBlock>

        <CollapseBlock
          collapsible={true}
          title={t('Príprava na budúci akademický rok')}
          style={{ marginTop: '10px' }}
        >
          <div style={{ marginTop: '16px' }}>
            <Typography.Text
              style={{ display: 'block', marginBottom: 8 }}
            >
              <strong>{t('Budúci akademický rok')}</strong>: {academicYear}/{academicYear + 1}
            </Typography.Text>
            {hasUnpublishedStudyPrograms && (
              <Alert
                type="error"
                showIcon
                style={{ marginTop: 16 }}
                title={t('Prípravu na budúci akademický rok nie je možné spustiť')}
                description={
                  <>
                    <p style={{ margin: '0 0 8px' }}>
                      {t('Priradenie študentov ešte nie je publikované pre tieto študijné programy:')}
                    </p>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {unpublishedStudyPrograms.map((studyProgram) => (
                        <li key={studyProgram.id}>
                          {studyProgram.name}
                          {studyProgram.shortcut ? ` (${studyProgram.shortcut})` : ''}
                        </li>
                      ))}
                    </ul>
                  </>
                }
              />
            )}
            <div style={{ marginTop: 16, marginBottom: 16 }}>
              <Button
                type="primary"
                onClick={() => setPrepareNewYearOpen(true)}
                loading={isPreparing}
                disabled={hasUnpublishedStudyPrograms}
              >
                {t('Príprava na budúci akademický rok')}
              </Button>
            </div>
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              title={t('Posledná príprava na nový akademický rok')}
              description={
                settings?.date_last_new_year_preparation
                  ? (
                    <>
                      {t('Operácia bola naposledy spustená')}:{' '}
                      <strong>
                        {dayjs(settings.date_last_new_year_preparation).format('DD.MM.YYYY HH:mm')}
                      </strong>
                    </>
                  )
                  : t('Operácia zatiaľ nebola spustená')
              }
            />
            <Alert
              type="info"
              showIcon
              title={t('Príprava na budúci akademický rok')}
              style={{ marginBottom: 16 }}
              description={
                <>
                  <p style={{ margin: '0 0 8px' }}>
                    {t('Operácia vykoná nasledujúce kroky:')}
                  </p>
                  <ul style={{ margin: '0 0 8px', paddingLeft: 20 }}>
                    <li>{t('nastaví nový akademický rok,')}</li>
                    <li>{t('vymaže zoznam prijatých študentov končiaceho akademického roka,')}</li>
                    <li>{t('vymaže prihlášky študentov, ktorí nie sú v zozname prijatých študentov a nemajú pridelený projekt,')}</li>
                    <li>{t('archivuje všetky žiadosti o zmenu projektu (aj čakajúce na schválenie), ktoré sa potom nebudú zobrazovať v zozname žiadostí,')}</li>
                    <li>{t('uloží dátum a čas spustenia operácie.')}</li>
                  </ul>
                  <p style={{ margin: '0 0 8px' }}>
                    {t('Priradenia študentov na projekty a samotné projekty zostávajú zachované. Od tejto operácie závisí, ako sa budú zobrazovať prijatí študenti na konkrétnom projekte, aj ako sa zobrazujú súčasní študenti na projektoch.')}
                  </p>
                  <p style={{ margin: 0 }}>
                    <strong>{t('Operácia by sa mala vykonať pred začiatkom nového akademického roka.')}</strong>
                  </p>
                </>
              }
            />
          </div>
        </CollapseBlock>

      </div>

      <QuestionModal
        visible={prepareNewYearOpen}
        toggle={() => setPrepareNewYearOpen(false)}
        headerTitle={t('Príprava na budúci akademický rok')}
        yes={t('Potvrdiť')}
        yesDanger
        yesLoading={isPreparing}
        chosenPositive={handlePrepareNewYear}
        question={
          <SeriousDeleteQuestion
            firstText={t(
              'Operácia nastaví akademický rok {{from}}/{{to}}, vymaže zoznam prijatých študentov končiaceho akademického roka, prihlášky študentov bez prideleného projektu, ktorí nie sú v zozname prijatých študentov, a archivuje všetky žiadosti o zmenu projektu.',
              { from: academicYear, to: academicYear + 1 },
            )}
            mainNotice={t('Naozaj chcete spustiť prípravu na budúci akademický rok?')}
            lastText={t('Túto akciu nie je možné vrátiť späť.')}
          />
        }
      />
    </>
  );
};

export default IngProjectsSettingsPage;
